import { Modal, Typography } from "@reconvert/react-ui-component";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Divider, Flex } from "@chakra-ui/react";
import { useAppSelector } from "../../app/hooks";
import { selectAppState } from "../../app/slice/appSlice";
import { PageNames } from "../../app/slice/appSlice.model";
import useConditions from "../../hooks/useConditions";
import { Slot } from "../../models";
import { SHOPIFY_API_KEY, SHOPIFY_APP_ID } from "../../config";
import { IFramePostMessageType, sendPostMessage } from "../../utils/helpers";
import { selectBannerState } from "../../app/slice/bannerSlice";
import { useDirection } from "../../hooks/useDirection";
import { PageSetup } from "@reconvert/react-ui-component";
import { useTranslation } from "react-i18next";

interface ChakraSlotSetupModalProps {
  isOpen: boolean;
  slot: Slot;
  onClose: () => void;
}

const ChakraSlotSetupModal = ({ isOpen, slot, onClose }: ChakraSlotSetupModalProps) => {
  const { t } = useTranslation();
  const { direction } = useDirection();
  const { activePage, isMobile } = useAppSelector(selectAppState);
  const { slotStatuses } = useAppSelector(selectBannerState);
  const { selectedCondition } = useConditions();
  const { checkoutProfileId } = useAppSelector(selectAppState);
  const [isGoToEditorClicked, setIsGoToEditorClicked] = useState(false);
  const [slotStatusInfo, setSlotStatusInfo] = useState<{
    isLoading: boolean;
    isSuccess: boolean;
    slotStatus: any;
  }>({
    isLoading: false,
    isSuccess: false,
    slotStatus: null,
  });

  const [continueButtonInfo, setContinueButtonInfo] = useState({
    isDisabled: true,
  });

  useEffect(() => {
    if (isOpen) {
      setSlotStatusInfo({
        isLoading: false,
        isSuccess: false,
        slotStatus: null,
      });
      setContinueButtonInfo({
        isDisabled: true,
      });
      setIsGoToEditorClicked(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (slotStatuses && slotStatuses?.length) {
      const selectedSlotStatus = slotStatuses.find(
        (status: any) => status?.location === activePage && status?.slotNo === slot?.slotNo,
      );

      if (!selectedSlotStatus) {
        setSlotStatusInfo(prev => ({
          ...prev,
          isLoading: false,
          isSuccess: true,
        }));
        setContinueButtonInfo({
          isDisabled: false,
        });
      } else {
        setSlotStatusInfo(prev => ({
          ...prev,
          isLoading: false,
          isSuccess: false,
          slotStatus: "detected",
        }));
      }
    }
  }, [activePage, slot?.slotNo, slotStatuses]);

  const checkStatus = useCallback(async () => {
    setSlotStatusInfo(prev => ({
      ...prev,
      isLoading: true,
    }));

    try {
      sendPostMessage(IFramePostMessageType.SYNC_SLOT_STATUS);
    } catch (error: any) {
      setSlotStatusInfo(prev => ({
        ...prev,
        isLoading: false,
        isSuccess: false,
        slotStatus: null,
      }));
    }
  }, []);

  const handleOnClose = useCallback(async () => {
    try {
      sendPostMessage(IFramePostMessageType.SYNC_SLOT_STATUS);
    } catch (error) {
      console.error("Error syncing slot status:", error);
    } finally {
      onClose();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBeforeNavigate = useCallback(() => {
    setIsGoToEditorClicked(true);
  }, []);

  const editorRedirectionConfig = useMemo(
    () => ({
      shopifyApiKey: SHOPIFY_API_KEY || "",
      platformStoreId: selectedCondition?.platformStoreId || "",
      checkoutProfileId: checkoutProfileId || "",
      shopifyAppId: SHOPIFY_APP_ID || "",
    }),
    [selectedCondition?.platformStoreId, checkoutProfileId],
  );

  if (!isOpen) return null;

  const pageName = activePage ? PageNames[activePage] : null;
  const modalTitle = pageName ? `${t("Add Upsell.com to your")} ${t(pageName)}` : t("Slot Setup");

  const Footer = () => {
    return (
      <Box display="flex" justifyContent="flex-end" gap="12px">
        <Button padding="8px 16px" variant="secondary" onClick={handleOnClose}>
          {t("I'll do it later")}
        </Button>
        <Button padding="8px 16px" variant="primary" onClick={handleOnClose} isDisabled={continueButtonInfo.isDisabled}>
          {t("Continue")}
        </Button>
      </Box>
    );
  };

  return (
    <PageSetupWrapper
      isOpen={isOpen}
      handleOnClose={handleOnClose}
      modalTitle={modalTitle}
      footer={<Footer />}
      isMobile={isMobile}
    >
      <Box width="100%" overflow="visible" flex="1" display="flex" flexDirection="column">
        <PageSetup
          pageType={activePage as any}
          isMobileView={isMobile ? true : false}
          onGoToPage={{
            onBeforeNavigate,
            isDisabled: slotStatusInfo.isSuccess,
          }}
          checkStatusButton={{
            onClick: checkStatus,
            isLoading: slotStatusInfo.isLoading,
            isDisabled: !isGoToEditorClicked,
            isSuccess: slotStatusInfo.isSuccess,
          }}
          widgetType={slot?.type}
          editorRedirectionConfig={editorRedirectionConfig}
          slotStatus={slotStatusInfo.slotStatus}
          slotNumber={slot?.slotNo}
          direction={direction}
        />
      </Box>
    </PageSetupWrapper>
  );
};

const PageSetupWrapper = ({
  children,
  isOpen,
  handleOnClose,
  modalTitle,
  footer,
  isMobile,
}: {
  children: React.ReactNode;
  isOpen: boolean;
  handleOnClose: () => void;
  modalTitle: string;
  footer: React.ReactNode;
  isMobile: boolean;
}) => {
  if (isMobile) {
    return (
      <>
        <Typography variant="body" mb="16px">
          {modalTitle}
        </Typography>

        <Divider my="16px" />
        {children}
        <Divider my="16px" />
        {footer}
      </>
    );
  }

  return (
    <Box>
      <Modal
        isOpen={isOpen}
        onClose={handleOnClose}
        closeOnEsc
        title={modalTitle}
        hideCloseButton={false}
        size="4xl"
        styles={{
          content: {
            overflow: "visible",
            maxWidth: "1020px",
            width: "fit-content",
          },
          body: {
            overflow: "visible",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginTop: "0px",
            marginBottom: "0px",
          },
        }}
        footer={
          <Flex
            margin="0 16px"
            justifyContent="space-between"
            alignItems="center"
            width="calc(100% - 64px)"
            minHeight="80px"
          >
            <Box />
            {footer}
          </Flex>
        }
      >
        {children}
      </Modal>
    </Box>
  );
};

export default ChakraSlotSetupModal;
